import React, { useState, useRef, useEffect } from 'react';
import evidenceAPI from '../services/evidenceAPI';
import api from '../api';
import { useProject } from '../contexts/ProjectContext';
import {
  Bot, Send, Search, FileText, ArrowRight, Sparkles, Link2, BookOpen, AlertTriangle,
  Upload, CheckCircle, Trash2, X
} from 'lucide-react';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

const SUGGESTED_QUERIES = [
  'What is our password policy?',
  'Do we have encryption in transit and at rest?',
  'Can contractors access production systems?',
  'What MFA methods do we support?',
  'What is our incident response SLA?',
];

type AiMessage = {
  role: 'user' | 'bot';
  text: string;
  sources?: string[];
  type?: string;
};

const AIAssistant: React.FC = () => {
  const { currentProject } = useProject();
  const [query, setQuery] = useState('');
  const [policyMode, setPolicyMode] = useState(false);
  const [messages, setMessages] = useState<AiMessage[]>([
    {
      role: 'bot',
      text: 'Hello! I can search your collected evidence, detect policy contradictions, and translate framework requirements. Ask me anything about your compliance posture.',
      type: 'info'
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [evidenceCount, setEvidenceCount] = useState<number>(0);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Upload state
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    evidenceAPI.getEvidenceItems().then(items => setEvidenceCount(items.length)).catch(() => {});
    if (currentProject?.id) loadDocuments();
  }, [currentProject?.id]);

  const loadDocuments = async () => {
    if (!currentProject?.id) return;
    try {
      const res = await api.get(`/api/policy-search/${currentProject.id}/documents`);
      if (res.data.success) setUploadedDocs(res.data.documents || []);
    } catch { /* ignore */ }
  };

  const handleFileUpload = async (file: File) => {
    if (!currentProject?.id) {
      setUploadError('Please select a project first');
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'Policy');
      const res = await api.post(`/api/policy-search/${currentProject.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.success) {
        await loadDocuments();
        setMessages(prev => [...prev, {
          role: 'bot',
          text: res.data.duplicate
            ? `ℹ️ "${file.name}" was already uploaded.`
            : `✅ "${file.name}" uploaded and indexed. Switch to Policy Docs mode to search it.`,
          type: res.data.duplicate ? 'warning' : 'success'
        }]);
        if (!res.data.duplicate) setPolicyMode(true);
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (docId: number) => {
    if (!currentProject?.id) return;
    try {
      await api.delete(`/api/policy-search/${currentProject.id}/documents/${docId}`);
      setUploadedDocs(prev => prev.filter(d => d.id !== docId));
    } catch { /* ignore */ }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleQuery = async (q?: string) => {
    const text = q || query;
    if (!text.trim() || loading) return;
    setMessages(prev => [...prev, { role: 'user', text }]);
    setQuery('');
    setLoading(true);
    try {
      if (policyMode && currentProject?.id) {
        // Policy document search mode
        const res = await api.post(`/api/policy-search/${currentProject.id}/search`, {
          query: text,
          filters: {},
          conversation_history: []
        });
        const data = res.data;
        const top = data.results?.slice(0, 3) || [];
        const answer = top.length > 0
          ? top.map((r: any, i: number) =>
              `[${i + 1}] ${r.document_name} (${r.confidence} ${r.confidence_score}%)\n${r.content?.slice(0, 300)}...`
            ).join('\n\n')
          : data.message || 'No matching policy documents found. Upload and process policy documents first.';
        const contradictionNote = data.contradictions?.length > 0
          ? `\n\n⚠️ ${data.contradictions.length} contradiction(s) detected between documents.`
          : '';
        setMessages(prev => [...prev, {
          role: 'bot',
          text: answer + contradictionNote,
          sources: top.map((r: any) => r.document_name),
          type: top.length > 0 ? 'success' : 'warning'
        }]);
      } else {
        // Evidence search mode (default)
        const result = await evidenceAPI.aiAssistantQuery(text);
        setMessages(prev => [...prev, {
          role: 'bot',
          text: result.answer,
          sources: result.sources,
          type: result.type
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'bot',
        text: 'AI assistant is unavailable. Please ensure the backend server is running.',
        type: 'warning'
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
            <Bot className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AI Assistant</h1>
            <p className="text-sm text-gray-500">Policy search, contradiction detection & framework translation</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-1.5">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-indigo-700 font-medium">{evidenceCount} evidence items loaded</span>
            </div>
            <AdminSecurityHelpButton screenId="ai-assistant" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Panel */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden">
          {/* Jarvis AI Assistant header */}
          <div className="bg-indigo-600 px-5 py-3 flex items-center gap-2">
            <Bot className="w-4 h-4 text-white" />
            <span className="text-sm font-semibold text-white">Jarvis AI Assistant</span>
            <div className="ml-auto flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-indigo-200">{evidenceCount} evidence items</span>
            </div>
          </div>
          {/* Messages */}
          <div className="flex-1 p-5 h-[240px] overflow-y-auto space-y-4 bg-gray-50">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'bot' && (
                  <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-indigo-600" />
                  </div>
                )}
                <div className="max-w-[80%]">
                  <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-br-sm'
                      : msg.type === 'warning'
                      ? 'bg-amber-50 border border-amber-200 text-amber-900 rounded-bl-sm'
                      : msg.type === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-900 rounded-bl-sm'
                      : 'bg-gray-50 border border-gray-200 text-gray-800 rounded-bl-sm'
                  }`}>
                    {msg.text}
                  </div>
                  {msg.sources && msg.sources.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {msg.sources.map((s, si) => (
                        <span key={si} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full px-2 py-0.5">
                          <FileText className="w-3 h-3" />
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center mr-2 flex-shrink-0">
                  <Bot className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1.5">
                    {[0, 150, 300].map(d => (
                      <div key={d} className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-100 space-y-2">
            {/* Mode toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPolicyMode(false)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${!policyMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              >
                <Search className="w-3 h-3" /> Evidence
              </button>
              <button
                onClick={() => setPolicyMode(true)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${policyMode ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              >
                <BookOpen className="w-3 h-3" /> Policy Docs
              </button>
              {policyMode && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Searching uploaded policy documents
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleQuery()}
                placeholder={policyMode ? 'Search policy documents, e.g. "Is MFA required?"' : 'Ask about your compliance posture, policies, or evidence gaps...'}
                className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={() => handleQuery()}
                disabled={loading || !query.trim()}
                className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Suggested */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Suggested Queries</p>
            <div className="space-y-2">
              {SUGGESTED_QUERIES.map((q, i) => (
                <button
                  key={i}
                  onClick={() => handleQuery(q)}
                  disabled={loading}
                  className="w-full text-left text-sm px-3 py-2.5 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 text-gray-700 transition-all flex items-center justify-between group"
                >
                  <span>{q}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-indigo-500 flex-shrink-0 ml-2" />
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-4">
            <p className="text-xs font-semibold text-indigo-700 mb-2 flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5" /> Searching across
            </p>
            <p className="text-2xl font-bold text-indigo-800">{evidenceCount}</p>
            <p className="text-xs text-indigo-600 mt-0.5">evidence items</p>
          </div>

          {/* Policy Document Upload */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> Policy Documents
            </p>

            {/* Drop zone */}
            <div
              className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFileUpload(f); }}
            >
              {uploading ? (
                <div className="flex items-center justify-center gap-2 text-indigo-600">
                  <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs">Uploading...</span>
                </div>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">Drop file or click to upload</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">PDF, Word, Excel, TXT</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); e.target.value = ''; }}
            />

            {uploadError && (
              <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                <X className="w-3 h-3" /> {uploadError}
              </div>
            )}

            {/* Uploaded docs list */}
            {uploadedDocs.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {uploadedDocs.map(doc => (
                  <div key={doc.id} className="flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-2.5 py-1.5 border border-gray-100">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    <span className="flex-1 truncate text-gray-700" title={doc.original_name}>{doc.original_name}</span>
                    <button onClick={() => handleDeleteDoc(doc.id)} className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {uploadedDocs.length === 0 && !uploading && (
              <p className="text-[10px] text-gray-400 mt-2 text-center">No documents uploaded yet</p>
            )}
          </div>

          {/* Capabilities */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Capabilities</p>
            <div className="space-y-2">
              {[
                { icon: '🔍', text: 'Search policies & evidence' },
                { icon: '⚠️', text: 'Detect contradictions' },
                { icon: '🔗', text: 'Framework translation' },
                { icon: '📊', text: 'Gap identification' },
              ].map(({ icon, text }) => (
                <div key={text} className="flex items-center gap-2 text-sm text-gray-600">
                  <span>{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Framework Translation Examples */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <Link2 className="w-3.5 h-3.5" /> Framework Translation
            </p>
            <p className="text-xs text-gray-500 mb-3">Same control, different auditor phrasing:</p>
            <div className="space-y-3">
              {[
                {
                  control: 'MFA Implementation',
                  color: 'indigo',
                  questions: ['Do you enforce MFA for all users?', 'Is two-step verification required?'],
                },
                {
                  control: 'Encryption at Rest',
                  color: 'purple',
                  questions: ['How is sensitive data encrypted?', 'Do you use AES-256 for stored data?'],
                },
                {
                  control: 'Incident Response',
                  color: 'rose',
                  questions: ['What is your IR SLA for critical events?', 'Describe your breach notification process.'],
                },
              ].map(({ control, color, questions }) => (
                <div key={control} className={`p-3 bg-${color}-50 border border-${color}-100 rounded-lg`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Sparkles className={`w-3.5 h-3.5 text-${color}-500`} />
                    <span className={`text-xs font-semibold text-${color}-700`}>{control}</span>
                  </div>
                  <div className="space-y-1">
                    {questions.map((q, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        <span className={`text-[10px] mt-0.5 font-bold text-${color}-400`}>Q{i + 1}</span>
                        <span className="text-xs text-gray-600">{q}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
